import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import toast from 'react-hot-toast';
import { useQueryClient, useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/hooks';
import { useGetCoursesSubjectsQuery } from '../../hooks/useGetCoursesSubjectsQuery';
import { deleteCourseSubject } from '../../services/school/courseSubject/deleteCourseSubject';
import TableComponent from '../../components/common/Tables';
import { Badge, Flex, Heading } from '@radix-ui/themes';
import { EmptyState } from '../../components/common/EmptyState';
import { Lists } from '../../components/common/Lists';
import { routes } from '../../routes';
import { resetPreviousPath } from '../../redux/slices/previousPath';

const columnNamesMap: { [key: string]: string } = {
   // course: 'Curso',
    subject: 'Disciplina',
    level: 'Ano ',
    semester: 'Semestre',
    isCoreBagde: 'Nuclear/Opcional',
};

export const CurriculumGrid = () => {
    const updateCourseFormData = useSelector((state: RootState) => state.course);
    const previousPath = useSelector((state: RootState) => state.previousPath);
    const [currentPage, setCurrentPage] = useState(1);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();

    const { data, status } = useGetCoursesSubjectsQuery(
        { PageNumber: currentPage, PageSize: 400, IdCourse: updateCourseFormData.id },
        { enabled: !!updateCourseFormData?.id }
    );

    const mutationDeleteCourseSubject = useMutation(deleteCourseSubject, {
        onSuccess: () => {
            queryClient.invalidateQueries('courses-subjects-pagination');
            toast.success('Disciplina eliminada do curso');
        },
        onError: () => {
            toast.error('Erro ao eliminar a disciplina do curso.');
        },
    });

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleBack = (e: any) => {
        const path = previousPath.previousPath!;
        dispatch(resetPreviousPath());
        navigate(path);
    };

    // Organizar dados por nível e semestre
    const groupedData = data?.items.reduce((acc: any, item: any) => {
        const levelKey = `${item.level}º Ano`;
        const semesterKey = ` ${item.semester}º Semestre`;

        if (!acc[levelKey]) acc[levelKey] = {};
        if (!acc[levelKey][semesterKey]) acc[levelKey][semesterKey] = [];

        acc[levelKey][semesterKey].push({
            ...item,
            isCoreBagde: item.isCore ? (
                <Badge color="green">Nuclear</Badge>
            ) : (
                <Badge color="gray">Opcional</Badge>
            ),
        });

        return acc;
    }, {});

    return (
        <Lists
            status={status}
            TableComponent={() =>
                <Flex direction="column" gap="5">
                    {groupedData && Object.keys(groupedData).length === 0 && (
                        <EmptyState
                            title="Grelha curricular por definir"
                            description="Ainda não existem disciplinas associadas a este curso."
                        />
                    )}
                    {groupedData &&
                        Object.keys(groupedData).map((level) => (
                            <Flex direction="column" gap="3" key={level}>
                                <Heading as="h2" size="4">{level}</Heading>
                                {Object.keys(groupedData[level]).map((semester) => (
                                    <Flex direction="column" gap="2" key={semester}>
                                        <Heading as="h3" size="3" color="gray">{semester}</Heading>
                                        <TableComponent
                                            columns={Object.keys(columnNamesMap)}
                                            items={groupedData[level][semester]}
                                            options={[]}
                                            columnNamesMap={columnNamesMap}
                                            totalPages={data?.totalPages || 1}
                                            currentPage={currentPage}
                                            onPageChange={handlePageChange}
                                        />
                                    </Flex>
                                ))}
                            </Flex>
                        ))}
                </Flex>
            }
            handleClickCreate={undefined}
            handleClickBack={handleBack}
        />
    );
};
